import { useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { useTransition } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { gql } from '@/lib/graphql/gql'
import type {
  DeleteWebhookMutation,
  DeleteWebhookMutationVariables,
} from '@/routes/webhook/tabs/overview/sections/danger-zone/delete-webhook/delete-webhook-dialog/index.types.gen'

const DELETE_WEBHOOK = gql`
  mutation DeleteWebhook($id: String!) {
    deleteWebhook(id: $id)
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const DeleteWebhookDialog = ({ isOpen, onClose }: Props) => {
  const { idWebhook = '' } = useParams()
  const [isPending, startTransition] = useTransition()

  const [deleteWebhook] = useMutation<
    DeleteWebhookMutation,
    DeleteWebhookMutationVariables
  >(DELETE_WEBHOOK)

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent onEscapeKeyDown={() => onClose()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete webhook</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete webhook?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={() => onClose()}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() => {
              startTransition(async () => {
                try {
                  await deleteWebhook({ variables: { id: idWebhook } })
                  toast(
                    <ToasterContent variant="success">
                      Webhook is deleted successfully
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                  onClose()
                } catch {
                  toast(
                    <ToasterContent variant="error">
                      Failed to delete the webhook
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                }
              })
            }}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
